import {
  type ChangePaymentMethodReason,
  type ReceiptClaimDraft,
  ReceiptClaimFlow,
} from "@nmible-ltd/claims";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Feature,
  getGetBootstrapForCurrentTenantBootstrapGetQueryKey,
  PaymentType as PayableType,
} from "@/api-v3";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useParticipantSession } from "@/features/auth";
import { useFeatureFlag } from "@/features/feature-flags";
import { setReturnTo } from "@/features/payment-methods";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch, useAppSelector } from "@/store";
import { AppRoute } from "@/types/routes";

import { PwaClaimsProvider } from "../../components/pwa-claims-provider";
import { useClaimDetour } from "../../hooks/use-claim-detour";
import {
  forgetPdfReceipt,
  keepPdfReceipt,
  selectPdfReceiptDraft,
} from "../../store/pdf-receipt-draft-slice";
import {
  patchReceiptClaimDraft,
  selectReceiptClaimDraft,
} from "../../store/receipt-claim-draft-slice";

/**
 * The receipt claim, mounted afresh by each navigation to its route. The top
 * nav's new claim menu shows on the claim page too, and a receipt claim started
 * from it there navigates to the same route, so the photo, PDF and AI refusal
 * the flow holds go with the draft the menu reset.
 */
export const SubmitReceiptPage = () => {
  const { key } = useLocation();
  return (
    <PwaClaimsProvider>
      <ReceiptClaim key={key} />
    </PwaClaimsProvider>
  );
};

const ReceiptClaim = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { participantId, currency, countryCode } = useParticipantSession();
  const receiptAiEnabled = useFeatureFlag(Feature.receipt_ai);
  const startClaimDetour = useClaimDetour(PayableType.receipt_claim);

  const dispatch = useAppDispatch();
  const memoryDraft = useAppSelector(selectReceiptClaimDraft);
  const keptPdf = useAppSelector(selectPdfReceiptDraft);

  // The PDF is kept on the device so a refresh brings it back, and the rest
  // of the claim is kept in memory only.
  const isOwnPdf =
    participantId !== null &&
    keptPdf.participantId === participantId &&
    keptPdf.pdfUrl !== null;
  const isAnotherParticipantsPdf = keptPdf.pdfUrl !== null && !isOwnPdf;

  // The flow only forgets a PDF it is given, so another participant's is
  // forgotten here, without asking for it.
  useEffect(() => {
    if (isAnotherParticipantsPdf) dispatch(forgetPdfReceipt());
  }, [isAnotherParticipantsPdf, dispatch]);

  const draft = useMemo<ReceiptClaimDraft>(
    () => ({
      ...memoryDraft,
      pdf:
        isOwnPdf && keptPdf.pdfUrl
          ? { pdfUrl: keptPdf.pdfUrl, selectedPages: keptPdf.selectedPages }
          : null,
    }),
    [memoryDraft, isOwnPdf, keptPdf.pdfUrl, keptPdf.selectedPages]
  );

  const onDraftChange = useCallback(
    (patch: Partial<ReceiptClaimDraft>) => {
      const { pdf, ...rest } = patch;
      if ("pdf" in patch) {
        dispatch(
          pdf && participantId
            ? keepPdfReceipt({ participantId, ...pdf })
            : forgetPdfReceipt()
        );
      }
      if (Object.keys(rest).length > 0) {
        dispatch(patchReceiptClaimDraft(rest));
      }
    },
    [dispatch, participantId]
  );

  // Only a refusal is a detour. Changing an account that can receive the claim
  // is a choice, and must not be met with a detour notice.
  const changePaymentMethod = (reason: ChangePaymentMethodReason) => {
    if (reason === "refused") {
      startClaimDetour();
      return;
    }
    dispatch(setReturnTo(pathname));
    navigate(AppRoute.PAYMENT_METHODS_LIST);
  };

  return (
    <>
      <h1 className="sr-only">{t("claim_chooseClaimReceipt")}</h1>
      {currency === null ? (
        <Alert variant="destructive">
          <CircleAlert />
          <AlertDescription>
            {t("claim_site_details_incomplete")}
          </AlertDescription>
        </Alert>
      ) : (
        <ReceiptClaimFlow
          currency={currency}
          countryCode={countryCode}
          draft={draft}
          onDraftChange={onDraftChange}
          aiExtractionEnabled={receiptAiEnabled}
          onReceiptAiDisabled={() => {
            void queryClient.invalidateQueries({
              queryKey: getGetBootstrapForCurrentTenantBootstrapGetQueryKey(),
            });
          }}
          onCancel={() => navigate(AppRoute.PAYMENTS, { replace: true })}
          onSubmitted={() => {
            toast({ description: t("claim_toastConfirmationMessage") });
            navigate("/payments", { replace: true });
          }}
          onChangePaymentMethod={changePaymentMethod}
        />
      )}
    </>
  );
};
